import { cn } from "@design-system/utils";
import { ChevronDown } from "lucide-react";
import * as React from "react";

import { useScrollToBottom } from "../../hooks/use-scroll-to-bottom";
import { Button } from "../button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "../command";
import { Loader } from "../loader";
import { Popover, PopoverContent, PopoverTrigger } from "../popover";

interface ComboboxContextValue {
  open: boolean;
  setOpen: (open: boolean) => void;
  value: string;
  setValue: (value: string) => void;
  searchValue: string;
  setSearchValue: (value: string) => void;
  onScrollToBottom?: () => void;
  isLoading: boolean;
  isLoadingMore: boolean;
  hasMoreOptions: boolean;
  disabled: boolean;
}

const ComboboxContext = React.createContext<ComboboxContextValue | undefined>(
  undefined
);

function useComboboxContext() {
  const context = React.useContext(ComboboxContext);
  if (!context) {
    throw new Error(
      "Combobox compound components must be used within Combobox"
    );
  }
  return context;
}

interface ComboboxProps extends Partial<
  Pick<
    ComboboxContextValue,
    | "onScrollToBottom"
    | "isLoading"
    | "isLoadingMore"
    | "hasMoreOptions"
    | "disabled"
  >
> {
  value: string;
  onChange: (value: string) => void;
  children: React.ReactNode;
  onSearch?: (value: string) => void;
}

export function Combobox({
  children,
  value,
  onChange,
  onSearch,
  onScrollToBottom,
  isLoading = false,
  isLoadingMore = false,
  hasMoreOptions = true,
  disabled = false,
}: ComboboxProps) {
  const [open, setOpen] = React.useState(false);
  const [searchValue, setSearchValue] = React.useState("");

  const handleSearchChange = React.useCallback(
    (value: string) => {
      setSearchValue(value);
      onSearch?.(value);
    },
    [onSearch]
  );

  // Prevent the popover from opening when the combobox is disabled. The
  // trigger Button's `disabled` attribute blocks pointer/keyboard activation,
  // but this guard also covers programmatic toggles via context (e.g. from
  // ComboboxListItem) so disabled is enforced at the source of truth.
  const handleOpenChange = React.useCallback(
    (next: boolean) => {
      if (disabled && next) return;
      setOpen(next);
    },
    [disabled]
  );

  // Close an already-open popover if `disabled` flips to true mid-flight.
  // Without this, blocking new opens in `handleOpenChange` would leave a
  // previously opened popover mounted and interactive on a disabled combobox.
  React.useEffect(() => {
    if (disabled && open) setOpen(false);
  }, [disabled, open]);

  const contextValue = React.useMemo(
    () => ({
      open,
      setOpen: handleOpenChange,
      value,
      setValue: onChange,
      searchValue,
      setSearchValue: handleSearchChange,
      onScrollToBottom,
      isLoading,
      isLoadingMore,
      hasMoreOptions,
      disabled,
    }),
    [
      open,
      handleOpenChange,
      value,
      onChange,
      searchValue,
      handleSearchChange,
      onScrollToBottom,
      isLoading,
      isLoadingMore,
      hasMoreOptions,
      disabled,
    ]
  );

  return (
    <ComboboxContext.Provider value={contextValue}>
      <Popover open={open} onOpenChange={handleOpenChange}>
        {children}
      </Popover>
    </ComboboxContext.Provider>
  );
}

interface TriggerProps extends Omit<
  React.ComponentPropsWithoutRef<typeof Button>,
  "asChild"
> {
  asChild?: boolean;
  placeholder?: string;
}

function ComboboxTrigger({
  children,
  placeholder = "Select...",
  className,
  asChild = false,
  disabled: disabledProp,
  ...props
}: TriggerProps) {
  const { value, open, disabled: disabledFromContext } = useComboboxContext();

  // Disabled if either the root or the trigger says so. A root `disabled`
  // cascades and cannot be overridden by an explicit `disabled={false}` on
  // the trigger, which matches how form-control disabled normally behaves.
  const disabled = disabledProp || disabledFromContext;

  const triggerContent = (
    <>
      <span className="pointer-events-none truncate">
        {children || value || placeholder}
      </span>
      <ChevronDown
        className={cn(
          "text-shape-neutral-outlined-foreground-default size-5 shrink-0 transition-transform duration-200",
          open && "rotate-180"
        )}
      />
    </>
  );

  return (
    <PopoverTrigger asChild>
      {asChild ? (
        children
      ) : (
        <Button
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          className={cn(
            // Base input-field styling
            "bg-shape-neutral-outlined-background rounded-lg border border-solid",
            "text-shape-neutral-outlined-foreground-default",
            "h-9 gap-2 px-3 py-2 font-normal",
            "flex w-full items-center justify-between",
            // Prevent button default hover/active states (scoped to enabled
            // state so the disabled `opacity-50` is not overridden)
            "enabled:hover:bg-shape-neutral-outlined-background enabled:active:bg-shape-neutral-outlined-background",
            "enabled:hover:opacity-100",
            // Border color - default and error state
            "border-shape-neutral-outlined-border-emphasised",
            "aria-invalid:border-shape-critical-outlined-border-emphasised",
            // Focus ring
            "focus-visible:ring-ring-default focus-visible:ring-3 focus-visible:outline-none",
            // Disabled state - matches Input/Select for consistency across
            // form controls
            "disabled:cursor-not-allowed disabled:opacity-50",
            className
          )}
          {...props}
        >
          {triggerContent}
        </Button>
      )}
    </PopoverTrigger>
  );
}

interface ContentProps extends React.ComponentPropsWithoutRef<
  typeof PopoverContent
> {
  highlightedValue?: string;
  onHighlightedValueChange?: (value: string) => void;
}

function ComboboxContent({
  children,
  className,
  highlightedValue,
  onHighlightedValueChange,
  ...props
}: ContentProps) {
  // Prevents scroll events from propagating to parent, fixing wheel scroll inside popover
  // https://github.com/shadcn-ui/ui/issues/607
  const handleWheel = React.useCallback(
    (e: React.WheelEvent) => e.stopPropagation(),
    []
  );

  return (
    <PopoverContent
      className={cn("w-[var(--radix-popover-trigger-width)] p-0", className)}
      align="start"
      onWheel={handleWheel}
      {...props}
    >
      <Command
        shouldFilter={false}
        value={highlightedValue}
        onValueChange={onHighlightedValueChange}
      >
        {children}
      </Command>
    </PopoverContent>
  );
}

type InputProps = Omit<
  React.ComponentPropsWithoutRef<typeof CommandInput>,
  "value" | "onValueChange"
>;

function ComboboxInput({
  placeholder = "Search...",
  className,
  ...props
}: InputProps) {
  const { searchValue, setSearchValue } = useComboboxContext();

  return (
    <CommandInput
      value={searchValue}
      onValueChange={setSearchValue}
      placeholder={placeholder}
      className={className}
      {...props}
    />
  );
}

type ListProps = React.ComponentPropsWithoutRef<typeof CommandList>;

function ComboboxList({ children, className, ...props }: ListProps) {
  const { onScrollToBottom, hasMoreOptions, isLoading, isLoadingMore } =
    useComboboxContext();
  const handleScroll = useScrollToBottom({
    onScrollToBottom,
    hasMoreOptions,
    isLoadingMore,
  });

  return (
    <CommandList className={className} onScroll={handleScroll} {...props}>
      {isLoading ? (
        <div className="flex justify-center px-2 py-3">
          <Loader />
        </div>
      ) : (
        <CommandGroup>{children}</CommandGroup>
      )}
      {isLoadingMore && (
        <div className="text-foreground-neutral-muted flex flex-row justify-center p-2 text-sm">
          <Loader />
        </div>
      )}
    </CommandList>
  );
}

type EmptyProps = React.ComponentPropsWithoutRef<typeof CommandEmpty>;

function ComboboxEmpty({ children, ...props }: EmptyProps) {
  return <CommandEmpty {...props}>{children}</CommandEmpty>;
}

interface ListItemProps extends Omit<
  React.ComponentPropsWithoutRef<typeof CommandItem>,
  "onSelect"
> {
  value: string;
}

function ComboboxListItem({
  value,
  children,
  className,
  ...props
}: ListItemProps) {
  const context = useComboboxContext();

  const handleSelect = React.useCallback(() => {
    context.setValue(value);
    context.setOpen(false);
  }, [context, value]);

  return (
    <CommandItem
      value={value}
      onSelect={handleSelect}
      className={className}
      {...props}
    >
      {children}
    </CommandItem>
  );
}

Combobox.Trigger = ComboboxTrigger;
Combobox.Content = ComboboxContent;
Combobox.Input = ComboboxInput;
Combobox.List = ComboboxList;
Combobox.ListItem = ComboboxListItem;
Combobox.Empty = ComboboxEmpty;

export {
  ComboboxTrigger,
  ComboboxContent,
  ComboboxInput,
  ComboboxList,
  ComboboxListItem,
  ComboboxEmpty,
};

export type { ComboboxProps };
